import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import RedButton from '../components/RedButton';
import Button from '../parts/button';
import BasicBCPStepFlow from '../components/BasicBCPStepFlow';
import { navigationService } from '../utils/navigation';
import { bcpService } from '../services/bcpService';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { PROCEDURE_MANUAL_SECTIONS } from '../config/procedureManualSections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import type { ProcedureManualFormData } from '../types/procedure-manual';
import { createInitialProcedureManualFormData } from '../types/procedure-manual';
import { generateStockpile } from '../services/api';
import BCPDownloadModal from '../components/BCPDownloadModal';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
  regulationData?: Record<string, string>;
  restorePolicyData?: Record<string, string>;
  initialResponseData?: Record<string, string>;
  procedureManualData?: ProcedureManualFormData;
}

const ProcedureManualPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState = navigationService.extractLocationState<NavigationState>(
    location,
    {
      uploadedFiles: [],
      apiResponse: undefined,
      companyInfo: undefined,
      regulationData: undefined,
      restorePolicyData: undefined,
      initialResponseData: undefined,
      procedureManualData: undefined,
    }
  );

  const { uploadedFiles, procedureManualData, restorePolicyData, companyInfo } = navigationState;

  const [downloadCompleted, setDownloadCompleted] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const { message: successMessage, showMessage: setSuccessMessage } = useAutoDismissMessage();
  const [isGeneratingStockpile, setIsGeneratingStockpile] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const hasGeneratedStockpile = useRef(false);
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);

  const bcpId = params.bcpId ?? 'new';
  const tenantIdFromQuery = searchParams.get('tenantId');

  // BCP ID and tenant ID resolved values
  const resolvedBcpId = bcpId;
  const resolvedTenantId = tenantIdFromQuery;

  // Load BCP document
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
  } = useBCPLoader({
    bcpId: bcpId === 'new' ? null : bcpId,
    tenantId: tenantIdFromQuery,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // 初期値を保持（変更検知の基準値として使用）
  const initialValuesRef = useRef<Record<string, string> | undefined>(undefined);

  // ダウンロード時に自動保存されたBCPを保持
  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(null);

  // Check if we have saved procedure manual data
  const hasSavedData = useMemo(() => {
    if (procedureManualData && Object.keys(procedureManualData).length > 0) {
      return true;
    }
    // Check if procedure_manual section exists in bcpDocument
    // This is more reliable than checking form_data, as form_data may contain
    // fields from other sections
    if (bcpDocument?.content?.sections?.procedure_manual) {
      return true;
    }
    return false;
  }, [procedureManualData, bcpDocument]);

  // Extract stockpile plan from saved data
  const stockpilePlan = useMemo(() => {
    // Try procedureManualData first
    if (procedureManualData && 'stockpilePlan' in procedureManualData) {
      return (procedureManualData as any).stockpilePlan;
    }
    // Try bcpDocument content.sections.procedure_manual (more reliable)
    if (bcpDocument?.content?.sections?.procedure_manual) {
      const section = bcpDocument.content.sections.procedure_manual;
      // stockpilePlanプロパティが存在する場合は、空文字列でも返す
      if ('stockpilePlan' in section) {
        return section.stockpilePlan;
      }
    }
    // Fallback to form_data for compatibility
    if (bcpDocument?.form_data && 'stockpilePlan' in bcpDocument.form_data) {
      return bcpDocument.form_data.stockpilePlan;
    }
    // データが存在しない場合はundefinedを返す
    return undefined;
  }, [procedureManualData, bcpDocument]);

  // Extract effective restore policy data
  const effectiveRestorePolicyData = useMemo(() => {
    if (restorePolicyData) {
      return restorePolicyData;
    }
    // Extract from bcpDocument if in edit mode
    if (bcpDocument?.content?.sections?.restore_policy) {
      return bcpDocument.content.sections.restore_policy;
    }
    // Fallback to form_data
    if (bcpDocument?.form_data) {
      const fields = ['company_info', 'important_business'];
      const extracted: Record<string, any> = {};
      fields.forEach(field => {
        if (bcpDocument.form_data![field]) {
          extracted[field] = bcpDocument.form_data![field];
        }
      });
      return Object.keys(extracted).length > 0 ? extracted : undefined;
    }
    return undefined;
  }, [restorePolicyData, bcpDocument]);

  // Extract effective apiResponse
  const effectiveApiResponse = useMemo(() => {
    if (navigationState.apiResponse) {
      return navigationState.apiResponse;
    }
    // Extract from bcpDocument if needed
    if (bcpDocument?.form_data) {
      const extracted: Record<string, any> = { extracted_info: {} };
      // Try to extract business type from company_info
      const companyInfo = bcpDocument.form_data.company_info;
      if (companyInfo) {
        const businessMatch = companyInfo.match(/事業内容[:\s]*([^\n]+)/);
        if (businessMatch) {
          extracted.extracted_info.business_type = businessMatch[1].trim();
        }
      }
      return extracted;
    }
    return navigationState.apiResponse;
  }, [navigationState.apiResponse, bcpDocument]);

  const form = useBCPDynamicForm({
    sections: PROCEDURE_MANUAL_SECTIONS,
    // stockpilePlanがundefinedでない場合は設定する(空文字列も含む)
    initialValues: stockpilePlan !== undefined ? { stockpilePlan } : {},
  });

  const { values, setFieldValue, errors: fieldErrors, validate } = form;

  // isDirtyを計算（初期値と現在の値を比較）
  const isDirty = useMemo(() => {
    if (!initialValuesRef.current) {
      return false;
    }
    return Object.keys(values).some(
      key => values[key] !== (initialValuesRef.current?.[key] || '')
    );
  }, [values]);

  // 離脱確認ダイアログ(フォームが変更されていない、またはエクスポート中はスキップ)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;
      return !isDirty || isSaving || downloadLoading || isGeneratingStockpile;
    },
  });

  // BCP ID/Tenant IDの変更を検知してリセット
  const bcpIdRef = useRef(resolvedBcpId);
  const tenantIdRef = useRef(resolvedTenantId);

  useEffect(() => {
    const bcpChanged = resolvedBcpId !== bcpIdRef.current;
    const tenantChanged = resolvedTenantId !== tenantIdRef.current;

    if (bcpChanged || tenantChanged) {
      console.log('[ProcedureManualPage] BCP or tenant changed, resetting state');
      bcpIdRef.current = resolvedBcpId;
      tenantIdRef.current = resolvedTenantId;
      hasLoadedInitialData.current = false;
      initialValuesRef.current = undefined;
    }
  }, [resolvedBcpId, resolvedTenantId]);

  const aiConsultContext = useAIConsultContext({
    sections: PROCEDURE_MANUAL_SECTIONS,
    values,
    bcpType: 'basic',
    onFieldValueChange: setFieldValue,
  });

  // Load saved data when in edit mode
  useEffect(() => {
    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      const rawData = getInitialFormData();
      // stockpilePlanが存在する場合は設定する(空文字列も含む)
      if ('stockpilePlan' in rawData && rawData.stockpilePlan !== undefined && rawData.stockpilePlan !== null) {
        setFieldValue('stockpilePlan', rawData.stockpilePlan);
        setSuccessMessage('BCPを編集モードで読み込みました。');

        // 初期値を更新（変更検知の基準値として使用）
        initialValuesRef.current = { stockpilePlan: rawData.stockpilePlan };
      }
      // Mark as loaded regardless of whether data exists
      // This allows generation to proceed if stockpilePlan is empty
      hasLoadedInitialData.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument]);

  // Extract company_info once for dependency tracking
  const companyInfoText = useMemo(() => {
    return effectiveRestorePolicyData?.company_info;
  }, [effectiveRestorePolicyData]);

  // Generate stockpile list from restore policy data
  useEffect(() => {
    const generateStockpileList = async () => {
      // Skip if stockpile plan already exists with actual content
      // 空文字列の場合は未生成として扱い、自動生成を実行する
      if (stockpilePlan !== undefined && stockpilePlan.trim() !== '') {
        console.log('[ProcedureManualPage] stockpilePlan already exists, skipping generation');
        return;
      }

      // In edit mode, wait for initial data to be loaded
      if (isEditMode && !hasLoadedInitialData.current) {
        console.log('[ProcedureManualPage] Waiting for initial data to load in edit mode');
        return;
      }

      // Skip if already generated
      if (hasGeneratedStockpile.current) {
        return;
      }

      // Skip if no company info
      if (!companyInfoText || typeof companyInfoText !== 'string') {
        return;
      }

      hasGeneratedStockpile.current = true;
      setIsGeneratingStockpile(true);

      try {
        console.log('Generating stockpile list from restore policy data:', companyInfoText);

        // Extract additional info from effectiveApiResponse and effectiveRestorePolicyData
        const extractedInfo = effectiveApiResponse?.extracted_info || {};

        // companyInfoが存在する場合は、それを優先的に使用
        let critical_business = '';
        let business_type = '';
        let location = '';
        let employees = '';

        if (companyInfo) {
          critical_business = companyInfo.criticalBusiness || '';
          business_type = companyInfo.businessDescription || '';
          location = companyInfo.location || '';
          employees = companyInfo.employeeCount || '';
        } else {
          // companyInfoがない場合は従来のロジック
          // Try to get critical_business from multiple sources
          // 1. effectiveRestorePolicyData.important_business (most reliable)
          // 2. extractedInfo.main_business (from CompanyInfoPage)
          // 3. extractedInfo.critical_business
          critical_business = effectiveRestorePolicyData?.important_business
            || extractedInfo.main_business
            || extractedInfo.critical_business
            || '';

          business_type = extractedInfo.business_type || extractedInfo.business_content || '';
          location = extractedInfo.address || extractedInfo.location || '';
          employees = extractedInfo.employees || '';
        }

        console.log('Stockpile generation data:', {
          critical_business,
          business_type,
          location,
          employees,
          sources: {
            from_restore_policy: !!effectiveRestorePolicyData?.important_business,
            from_main_business: !!extractedInfo.main_business,
            from_critical_business: !!extractedInfo.critical_business
          }
        });

        const response = await generateStockpile({
          company_info: companyInfoText,
          critical_business,
          business_type,
          location,
          employees,
        });

        if (response.stockpile_plan) {
          console.log('Stockpile list generated successfully');
          setFieldValue('stockpilePlan', response.stockpile_plan);
        }
      } catch (error) {
        console.error('Failed to generate stockpile list:', error);
        setGeneralErrors(['備蓄品リストの生成に失敗しました。']);
        hasGeneratedStockpile.current = false;
      } finally {
        setIsGeneratingStockpile(false);
      }
    };

    generateStockpileList();
  }, [companyInfoText, stockpilePlan, setFieldValue, effectiveApiResponse, effectiveRestorePolicyData, companyInfo, isEditMode, hasSavedData]);

  const handleDownload = async () => {
    // 保存中または他のAPI呼び出し中は早期リターン
    if (isSaving || isGeneratingStockpile) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に自動保存
    setIsSaving(true);
    try {
      console.log('[ProcedureManualPage] Auto-saving before download...');
      const result = await bcpService.saveBasicSection(
        bcpDocument?.id || null,
        'procedure_manual',
        values,
        { current_step: 'procedure_manual' },
        tenantIdFromQuery || undefined,
        'published'
      );
      console.log('[ProcedureManualPage] Auto-save successful:', result.id);
      setAutoSavedBcp(result);

      // 保存成功後、現在の値を新しい初期値として設定
      initialValuesRef.current = { ...values };
      console.log('[ProcedureManualPage] Updated initialValuesRef after auto-save');
    } catch (error) {
      console.error('[ProcedureManualPage] Auto-save failed:', error);
      // エラーは表示しない（保存ボタンから保存できるため）
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setFieldValue(fieldId, value);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSave = async () => {
    // 保存中または他のAPI呼び出し中は早期リターン
    if (isSaving || isGeneratingStockpile) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setIsSaving(true);
    setSubmitLoading(true);
    setSuccessMessage(null);

    try {
      const sectionData = {
        ...values,
        downloadCompleted,
      };

      const result = await bcpService.saveBasicSection(
        bcpId === 'new' ? null : bcpId,
        'procedure_manual',
        sectionData,
        { current_step: 'procedure_manual' }
      );

      // 保存成功後、現在の値を新しい初期値として設定
      initialValuesRef.current = { ...values };

      // Update bcpId if this was a new document
      if (bcpId === 'new' && result.id) {
        navigate(`/bcp/${result.id}/procedure-manual`, {
          state: {
            ...location.state,
            bcpId: result.id,
          },
          replace: true,
        });
      }

      setSuccessMessage('手順書データを保存しました。');
    } catch (error) {
      console.error('Error saving procedure manual:', error);
      setGeneralErrors(['手順書データの保存に失敗しました。']);
    } finally {
      setSubmitLoading(false);
      setIsSaving(false);
    }
  };

  const handleSubmit = () => {
    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    isNavigatingRef.current = true;

    setCurrentStep(3);
    setIsDownloadModalOpen(true);
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors]
  );

  return (
    <>
    <BCPFormLayout
      currentStep={currentStep}
      showHeaderActions={true}
      onSave={handleSave}
      onDownload={handleDownload}
      saveLoading={submitLoading}
      downloadLoading={downloadLoading}
      saveDisabled={isGeneratingStockpile}
      downloadDisabled={isGeneratingStockpile}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType="basic"
      topContent={
        <BasicBCPStepFlow
          currentStep="procedure-manual"
          bcpId={bcpId}
          tenantId={tenantIdFromQuery}
        />
      }
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle title="手順書" />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {isGeneratingStockpile && (
              <Alert
                type="info"
                message="備蓄品リストを生成しています..."
              />
            )}

            {successMessage && <Alert type="success" message={successMessage} />}

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}

            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}
          </div>
        }
        footer={
          <>
            <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
            <div className="mt-8 flex justify-center gap-5">
              <RedButton
                onClick={() => {
                  // 「戻る」ボタンでのナビゲーション開始（ブロック解除）
                  isNavigatingRef.current = true;
                  navigate(`/bcp/${bcpId}/initial-response`, { state: location.state });
                }}
              >
                戻る
              </RedButton>
              <RedButton onClick={handleSubmit} loading={submitLoading}>
                次へ
              </RedButton>
            </div>
          </>
        }
      >
        {PROCEDURE_MANUAL_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(2);
      }}
      bcpType="basic"
      bcpId={autoSavedBcp?.id || bcpDocument?.id || (bcpId !== 'new' ? bcpId : null)}
    />
  </>
  );
};

export default ProcedureManualPage;
